/**
 * Azure Blob Storage API - Frontend Service
 * This service communicates with the backend API for file operations
 * Backend handles Azure credentials securely
 */

import api from './axiosConfig';
import { getConfiguredApiUrl } from '../config/runtimeConfig';

const API_BASE_URL = getConfiguredApiUrl();

export interface BlobUploadResponse {
  success: boolean;
  message: string;
  fileUrl?: string;
  blobName?: string;
  containerName?: string;
}

export interface SasTokenResponse {
  sasToken: string;
  blobUri: string;
  expiresIn: number;
}

export interface BlobFileInfo {
  name: string;
  url: string;
  createdAt: string;
  size: number;
  contentType: string;
}

/**
 * Get SAS token for secure file upload
 * TenantId is automatically extracted from auth context
 */
export const getBlobSasToken = async (
  containerName: string,
  blobName: string,
  expirationMinutes: number = 30
): Promise<SasTokenResponse> => {
  try {
    const response = await api.get(`${API_BASE_URL}/blob-storage/sas-token`, {
      params: {
        containerName,
        blobName,
        expirationMinutes,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting SAS token:', error);
    throw error;
  }
};

/**
 * Upload file to Azure Blob Storage via backend
 * @param containerName - e.g., 'documents', 'employee-images', 'attachments'
 * @param file - File object to upload
 * @param folderPath - Optional folder path within container (e.g., 'tenant-123/employees')
 */
export const uploadFileToBlobStorage = async (
  containerName: string,
  file: File,
  folderPath?: string
): Promise<BlobUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('containerName', containerName);
    if (folderPath) {
      formData.append('folderPath', folderPath);
    }

    const response = await api.post(
      `${API_BASE_URL}/blobstorage/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error uploading file to blob storage:', error);
    throw error;
  }
};

/**
 * Delete file from Azure Blob Storage via backend
 */
export const deleteFileFromBlobStorage = async (
  containerName: string,
  blobName: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post(
      `${API_BASE_URL}/blobstorage/delete`,
      {
        containerName,
        blobName,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting file from blob storage:', error);
    throw error;
  }
};

/**
 * List files in a container/folder (tenant-specific)
 */
export const listBlobFiles = async (
  containerName: string,
  folderPath?: string
): Promise<BlobFileInfo[]> => {
  try {
    const response = await api.get(`${API_BASE_URL}/blobstorage/files`, {
      params: {
        containerName,
        folderPath,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error listing blob files:', error);
    throw error;
  }
};

/**
 * Create container if not exists (admin only)
 */
export const createBlobContainer = async (
  containerName: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post(
      `${API_BASE_URL}/blobstorage/container`,
      {
        containerName,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating blob container:', error);
    throw error;
  }
};

/**
 * Generate container name with tenant isolation
 * Format: {category}-{tenantId}
 * Examples: employees-t123, documents-t123, attachments-t123
 */
export const generateTenantContainerName = (
  category: string,
  tenantId: string
): string => {
  return `${category.toLowerCase()}-${tenantId.toLowerCase()}`;
};

/**
 * Generate folder path for tenant organization
 * Format: tenant-{tenantId}/entity/{entityType}/{entityId}
 */
export const generateTenantFolderPath = (
  tenantId: string,
  entityType: string,
  entityId?: string
): string => {
  let path = `tenant-${tenantId}/${entityType}`;
  if (entityId) {
    path += `/${entityId}`;
  }
  return path;
};

/**
 * Generate unique blob name with timestamp
 */
export const generateUniqueBlobName = (originalFileName: string): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const fileExtension = originalFileName.split('.').pop();
  return `${timestamp}-${random}.${fileExtension}`;
};

// Blob categories for SaaS application
export enum BlobCategory {
  EMPLOYEE_DOCUMENTS = 'employee-documents',
  EMPLOYEE_IMAGES = 'employee-images',
  PAYROLL_REPORTS = 'payroll-reports',
  ATTENDANCE_REPORTS = 'attendance-reports',
  BGV_DOCUMENTS = 'bgv-documents',
  GENERAL_ATTACHMENTS = 'attachments',
  BIOMETRIC_LOGS = 'biometric-logs',
  LEAVE_DOCUMENTS = 'leave-documents',
}

// File size limits (in bytes)
export const FILE_SIZE_LIMITS = {
  IMAGES: 5 * 1024 * 1024, // 5MB
  DOCUMENTS: 10 * 1024 * 1024, // 10MB
  REPORTS: 50 * 1024 * 1024, // 50MB
  VIDEOS: 100 * 1024 * 1024, // 100MB
};

// Allowed MIME types
export const ALLOWED_MIME_TYPES = {
  IMAGES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  DOCUMENTS: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  ALL: [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/msword',
    'application/vnd.ms-excel',
  ],
};
